import React, { useState, useEffect } from 'react';
import {
  adminMetricsService,
  ActiveUsersMetric,
  AvgAdherenceMetric,
  UsersWithCustomRemindersMetric,
  AvgChatMessagesMetric,
  MealRecordPhotoVsDescriptionMetric,
  ActiveUsersWithNotificationsMetric,
} from '../../services/adminMetricsService';
import MetricCard from './MetricCard';

interface AdminMetricsProps {
  totalPatients: number;
}

const UsersIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
    <circle cx="9" cy="7" r="4"></circle>
    <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
    <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
  </svg>
);

const AdminMetrics: React.FC<AdminMetricsProps> = ({ totalPatients }) => {
  const [activeUsers, setActiveUsers] = useState<ActiveUsersMetric | null>(null);
  const [avgAdherence, setAvgAdherence] = useState<AvgAdherenceMetric | null>(null);
  const [customReminders, setCustomReminders] = useState<UsersWithCustomRemindersMetric | null>(null);
  const [avgChatMessages, setAvgChatMessages] = useState<AvgChatMessagesMetric | null>(null);
  const [mealRecordPhotoVsDescription, setMealRecordPhotoVsDescription] = useState<MealRecordPhotoVsDescriptionMetric | null>(null);
  const [activeUsersWithNotifications, setActiveUsersWithNotifications] = useState<ActiveUsersWithNotificationsMetric | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const fetchMetrics = async () => {
      try {
        setLoading(true);
        const [
          activeUsersData,
          avgAdherenceData,
          customRemindersData,
          avgChatMessagesData,
          mealRecordData,
          activeUsersWithNotificationsData,
        ] = await Promise.all([
          adminMetricsService.getActiveUsers(),
          adminMetricsService.getAvgAdherence(),
          adminMetricsService.getUsersWithCustomReminders(),
          adminMetricsService.getAvgChatMessages(),
          adminMetricsService.getMealRecordPhotoVsDescription(),
          adminMetricsService.getActiveUsersWithNotifications(),
        ]);
        setActiveUsers(activeUsersData);
        setAvgAdherence(avgAdherenceData);
        setCustomReminders(customRemindersData);
        setAvgChatMessages(avgChatMessagesData);
        setMealRecordPhotoVsDescription(mealRecordData);
        setActiveUsersWithNotifications(activeUsersWithNotificationsData);
      } catch (err) {
        console.error('Failed to fetch admin metrics:', err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchMetrics();
  }, []);

  const formatSinceDate = (since: string) => {
    const date = new Date(since);
    return date.toLocaleDateString('es-ES', { day: 'numeric', month: 'long' });
  };

  return (
    <div style={{ marginBottom: 40 }}>
      <h2 style={{ fontSize: 20, fontWeight: 700, color: '#1f2937', marginTop: 0, marginBottom: 20 }}>
        Métricas de Administración
      </h2>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: 20,
        }}
      >
        <MetricCard
          title="Total Pacientes"
          iconBg="#f0fdfa"
          iconColor="#38b2ac"
          icon={<UsersIcon />}
          value={<span style={{ fontSize: 36, fontWeight: 700, color: '#1f2937' }}>{totalPatients}</span>}
        />

        <MetricCard
          title="Usuarios Activos"
          iconBg="#eff6ff"
          iconColor="#3b82f6"
          icon={<UsersIcon />}
          loading={loading}
          error={error}
          subtitle="Usuarios que han registrado al menos una acción en la agenda en la última semana"
          value={
            <>
              <span style={{ fontSize: 36, fontWeight: 700, color: '#1f2937' }}>
                {activeUsers?.activeUsers}
              </span>
              {activeUsers?.since && (
                <span style={{ fontSize: 14, color: '#6b7280', fontWeight: 500 }}>
                  desde {formatSinceDate(activeUsers.since)}
                </span>
              )}
            </>
          }
        />

        <MetricCard
          title="Adherencia Media"
          iconBg="#faf5ff"
          iconColor="#8b5cf6"
          icon={
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
            </svg>
          }
          loading={loading}
          error={error}
          subtitle="Adherencia media de los usuarios activos en los ultimos 3 meses"
          value={
            <span style={{ fontSize: 36, fontWeight: 700, color: '#1f2937' }}>
              {avgAdherence?.avgAdherence != null ? `${avgAdherence.avgAdherence.toFixed(1)}%` : '-'}
            </span>
          }
        />

        <MetricCard
          title="Recordatorios Personalizados"
          iconBg="#fff7ed"
          iconColor="#f97316"
          icon={
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
              <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
            </svg>
          }
          loading={loading}
          error={error}
          subtitle="Usuarios que han configurado al menos un recordatorio personalizado"
          value={
            <span style={{ fontSize: 36, fontWeight: 700, color: '#1f2937' }}>
              {customReminders?.usersWithCustomReminders ?? '-'}
            </span>
          }
        />

        <MetricCard
          title="Mensajes de Chat (Media)"
          iconBg="#f0fdf4"
          iconColor="#22c55e"
          icon={
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
            </svg>
          }
          loading={loading}
          error={error}
          subtitle="Media de mensajes de chat por usuario activo"
          value={
            <>
              <span style={{ fontSize: 36, fontWeight: 700, color: '#1f2937' }}>
                {avgChatMessages?.avgChatMessages != null ? avgChatMessages.avgChatMessages.toFixed(1) : '-'}
              </span>
              {avgChatMessages?.since && (
                <span style={{ fontSize: 14, color: '#6b7280', fontWeight: 500 }}>
                  desde {formatSinceDate(avgChatMessages.since)}
                </span>
              )}
            </>
          }
        />

        <MetricCard
          title="Registros con Foto"
          iconBg="#fdf4ff"
          iconColor="#a855f7"
          icon={
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
              <circle cx="8.5" cy="8.5" r="1.5"></circle>
              <polyline points="21 15 16 10 5 21"></polyline>
            </svg>
          }
          loading={loading}
          error={error}
          subtitle="Porcentaje de registros de comida que incluyen foto"
          value={
            <>
              <span style={{ fontSize: 36, fontWeight: 700, color: '#1f2937' }}>
                {mealRecordPhotoVsDescription?.photoPercentage != null
                  ? mealRecordPhotoVsDescription.photoPercentage.toFixed(1)
                  : '-'}%
              </span>
              {mealRecordPhotoVsDescription?.total != null && (
                <span style={{ fontSize: 14, color: '#6b7280', fontWeight: 500 }}>
                  de {mealRecordPhotoVsDescription.total} registros
                </span>
              )}
            </>
          }
        />

        <MetricCard
          title="Activos con Notificaciones"
          iconBg="#eff6ff"
          iconColor="#6366f1"
          icon={
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 17H2a3 3 0 0 0 3-3V9a7 7 0 0 1 14 0v5a3 3 0 0 0 3 3zm-8.27 4a2 2 0 0 1-3.46 0"></path>
            </svg>
          }
          loading={loading}
          error={error}
          subtitle="Usuarios activos que tienen notificaciones habilitadas"
          value={
            <>
              <span style={{ fontSize: 36, fontWeight: 700, color: '#1f2937' }}>
                {activeUsersWithNotifications?.percentage != null
                  ? activeUsersWithNotifications.percentage.toFixed(1)
                  : '-'}%
              </span>
              {activeUsersWithNotifications?.withNotifications != null && activeUsersWithNotifications?.activeUsers != null && (
                <span style={{ fontSize: 14, color: '#6b7280', fontWeight: 500 }}>
                  {activeUsersWithNotifications.withNotifications}/{activeUsersWithNotifications.activeUsers} usuarios
                </span>
              )}
            </>
          }
        />
      </div>
    </div>
  );
};

export default AdminMetrics;
